
import { useRef, useEffect, useMemo } from "react";
import ChatLandingView from "./ChatLandingView";
import ChatCustomContent from "./ChatCustomContent";
import ChatCustomAction, { RetryAction } from "./ChatCustomAction";
import { usGenerativeChatStore } from "../store/generativeChatStore";
import useChatSend from "../hooks/useChatSend";
import ChatBox from "./ChatBox/ChatBox";
import { ScrollBarRef } from "@/custolink-views/asset_frontend/asset_view/asset_components/ai_support/components/ChatBox";
import { useTranslation } from "react-i18next";

export type ChatServerResponseContextFile = {
  filename: string;
  href: string;
  pages: string;
};
export type ChatServerResponse = {
  response: string;
  contextFiles: Array<ChatServerResponseContextFile>;
};

const ChatView = (p: {
  chatPrompt: (query: string) => Promise<ChatServerResponse>;
  /** When provided, replaces the default useChatSend send handler (e.g. for streaming). */
  handleSendOverride?: (prompt: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<ScrollBarRef>(null);
  // Tracks whether the transcript was at (or near) the bottom the last time
  // it was scrolled, so a streaming answer keeps following the tokens down
  // unless the visitor has deliberately scrolled up to re-read something -
  // in that case we leave the viewport alone rather than yanking it back.
  const isNearBottomRef = useRef(true);
  const { selectedConversation, chatHistory, isTyping, disabledChatFresh } =
    usGenerativeChatStore();
  const { handleSend: handleSendDefault } = useChatSend(p.chatPrompt);
  const handleSend = p.handleSendOverride ?? handleSendDefault;

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleTranscriptScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 80;
  };

  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [selectedConversation, chatHistory]);

  const messageList = useMemo(() => {
    const chat = chatHistory.find((chat) => chat.id === selectedConversation);
    return chat?.conversation || [];
  }, [selectedConversation, chatHistory]);

  const handleInputChange = async ({
    value,
  }: {
    value: string;
    attachments?: File[];
  }) => {
    await handleSend(value);
  };

  const handleFinish = (id: string) => {
    disabledChatFresh(id);
    scrollToBottom();
  };

  return (
    <div className="h-full flex-1">
      <ChatBox
        ref={scrollRef}
        messageList={messageList}
        placeholder={t('component.aisupport.aiprompt.yourInput')}
        showMessageList={Boolean(selectedConversation)}
        containerClass="h-full"
        inputFootnote={t('component.aisupport.disclaimer')}
        typing={
          isTyping
            ? {
                id: "ai",
                name: "Chat AI",
                avatarImageUrl: "/img/thumbs/ai.jpg",
              }
            : false
        }
        customRenderer={(message) => {
          if (message.sender.id === "ai") {
            return (
              <ChatCustomContent
                content={message.content as string}
                contextFiles={message.contextFiles}
                triggerTyping={message.fresh ? message.fresh : false}
                onFinish={() => handleFinish(message.id)}
              />
            );
          }

          return message.content;
        }}
        customAction={(message) => {
          if (message.sender.id !== "ai") {
            return null;
          }

          if (message.error) {
            const index = messageList.findIndex((m) => m.id === message.id);
            const priorUserMessage = messageList
              .slice(0, index)
              .reverse()
              .find((m) => m.isMyMessage && typeof m.content === "string");

            if (!priorUserMessage) {
              return null;
            }

            const prompt = priorUserMessage.content as string;
            return <RetryAction onRetry={() => void handleSend(prompt)} />;
          }

          if (!message.streaming) {
            return <ChatCustomAction content={message.content as string} />;
          }

          return null;
        }}
        onScroll={handleTranscriptScroll}
        onInputChange={handleInputChange}
      >
        {!selectedConversation && <ChatLandingView chatPrompt={p.chatPrompt} handleSendOverride={p.handleSendOverride} />}
      </ChatBox>
    </div>
  );
};

export default ChatView;
